import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { FormContainer, FormFieldRow, FormFieldColumn, FormSection, FormField, FormInputFull, FormInput, FormTextArea, FormLabel, FormSubHeading, FormItalicHeading, FormSectionPadding, CheckboxStyling } from '../../../styles/form-styles.js';
import colors from '../../../styles/colors.js';
import ABButton from '../../../components/ABButton.js';
import { history } from '../../../routers/AppRouter.js';
import ABInputField from '../../../components/ABInputField';
import { AlertPatent } from './alert-types.js';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import ABDropzone from '../../../components/ABDropzone';
import { ToastContainer, toast } from 'react-toastify';
import { Client } from '../../../types/types.js';

const PageWrapper = styled.div`
  margin: 0 auto;
  padding: 20px;
  min-height: 900px;
`;

const FormFlex = styled.div`
    display: flex;
    justify-content: space-between;
    width: 750px;
`;

const AlertFormLeft = styled.div`
    /* width: 360px; */
`;

const AlertFormRight = styled.div`
    /* width: 360px; */
    padding-left: 40px;
`;

const ImageSizeContainer = styled.div`
    display: flex;
`;

const PageHeading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 20px;
`;

const PageTitle = styled.div`
  color: ${colors.blue};
  font-size: 30px;
  padding: 0 0 10px 0;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 40px 0;
  div:first-child {
    padding-right: 10px;
  }
`;

const RightButton = styled.div`
  display: flex;
  justify-content: space-around;
`;

const StyledTextArea = styled.textarea`
  font-size: 16px;
  padding: 10px;
`;

const DefaultImagePreviewStyle = styled.div`
  img {
    width: 100px;
    height: 100px;
  }
`;

const StyledSelect = styled.select`
  font-size: 16px;
  padding: 9px 10px 10px 10px;
  width: 450px;
  border: 1px solid ${colors.lightGray};
`;

interface AlertPatentProps {
    alertItemId: number;
    pageTitle: string;
    handleSave: (alertPatent: AlertPatent) => void;
    handleUpdate: (alertPatent: AlertPatent) => void;
    alertPatent: AlertPatent
    clients: Client[];
}

const toolbarConfig = {
    options: [
        'inline',
        'textAlign',
        'link'
    ],
    inline: {
        options: ['bold', 'italic', 'underline'],
    },
    textAlign: {
        options: ['left', 'center', 'right', 'justify'],
    },
    link: {
        options: ['link', 'unlink'],
    }
};

const AlertPatentForm: React.FC<AlertPatentProps> = ({ alertPatent, alertItemId, pageTitle, handleSave, handleUpdate, clients }) => {

    const [id, setId] = useState(0);
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [image, setImage] = useState('');
    const [imageHeight, setImageHeight] = useState(0);
    const [imageWidth, setImageWidth] = useState(0);
    const [imageUploaded, setImageUploaded] = useState(false);
    const [link, setLink] = useState('');
    const [imageLink, setImageLink] = useState('');
    const [viewMoreLink, setViewMoreLink] = useState('');
    const [attribution, setAttribution] = useState('');
    const [clientId, setClientId] = useState(0);
    const [featuredContent, setFeaturedContent] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editorState, setEditorState] = useState(EditorState.createEmpty());

    useEffect(() => {
        // check if editing
        if (alertPatent.id !== 0) {
            setId(alertPatent.id);
            setEditing(true);
            setTitle(alertPatent.title);
            setBody(alertPatent.body);
            setImage(alertPatent.image);
            setImageHeight(alertPatent.imageHeight || 0);
            setImageWidth(alertPatent.imageWidth || 0);
            setLink(alertPatent.link);
            setImageLink(alertPatent.imageLink);
            // setViewMoreLink(alertPatent.viewMoreLink);
            setAttribution(alertPatent.attribution);
            setClientId(alertPatent.clientId || 0)
            const contentBlock = htmlToDraft(alertPatent.body);
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
            setEditorState(EditorState.createWithContent(contentState));
            if (alertPatent.featuredContent) {
                setFeaturedContent(alertPatent.featuredContent);
            }
        }
    }, [])

    const onSave = () => {

        if (imageUploaded && !image) {
            toast.error('Error Uploading Image');
            return;
        }

        const editorBody = draftToHtml(convertToRaw(editorState.getCurrentContent()))

        const alertPatent: AlertPatent = {
            id,
            title,
            body: editorBody,
            image,
            link,
            imageLink,
            alertItemId: alertItemId,
            attribution,
            clientId,
        }

        if (imageHeight !== null && imageHeight !== 0) {
            alertPatent.imageHeight = imageHeight
        }
        if (imageWidth !== null && imageWidth !== 0) {
            alertPatent.imageWidth = imageWidth
        }
        if (featuredContent) {
            alertPatent.featuredContent = featuredContent
        }

        if (editing) {
            handleUpdate(alertPatent);
        } else {
            handleSave(alertPatent);
        }
    };

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const title = e.currentTarget.value;
        setTitle(title);
    };

    const handleImageHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const imageHeight = e.currentTarget.value;
        setImageHeight(parseInt(imageHeight, 10));
    };

    const handleImageWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const imageWidth = e.currentTarget.value;
        setImageWidth(parseInt(imageWidth, 10));
    };

    const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const link = e.currentTarget.value;
        setLink(link);
    };

    const handleImageLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const imageLink = e.currentTarget.value;
        setImageLink(imageLink);
    };

    const handleViewMoreLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const viewMoreLink = e.currentTarget.value;
        setViewMoreLink(viewMoreLink);
    };

    const handleAttributionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const attribution = e.currentTarget.value;
        setAttribution(attribution);
    };

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setClientId(parseInt(e.target.value));
    }

    const onEditorStateChange = (editorState: any) => {
        setEditorState(editorState);
    };

    const handleFilesDrop = (cloudfrontUrl: string) => {
        setImage(cloudfrontUrl);
        setImageUploaded(true);
    };

    const handleFeaturedToggle = () => {
        setFeaturedContent((prevIsOn) => !prevIsOn);
    };

    return (
        <>
            <PageWrapper>
                <PageHeading>
                    <PageTitle>{pageTitle}</PageTitle>
                </PageHeading>

                <FormFlex>
                    <AlertFormLeft>
                        <FormSubHeading>Title:</FormSubHeading>
                        <ABInputField
                            value={title}
                            onChange={handleTitleChange}
                            placeholder="Title"
                        />

                        <FormSubHeading>Body:</FormSubHeading>
                        <Editor
                            editorState={editorState}
                            toolbarClassName="toolbarClassName"
                            wrapperClassName="wrapperClassName"
                            editorClassName="post-editor"
                            onEditorStateChange={onEditorStateChange}
                            toolbar={toolbarConfig}
                            stripPastedStyles={true}
                        />
                    </AlertFormLeft>

                    <AlertFormRight>
                        <FormSubHeading>Image:</FormSubHeading>
                        <ABDropzone onFilesDrop={handleFilesDrop} maxWidth={600} />

                        {
                            (image && !imageUploaded) &&
                            <DefaultImagePreviewStyle>
                                <FormSubHeading>Image Preview:</FormSubHeading>
                                <img src={`https://${image}`} />
                            </DefaultImagePreviewStyle>
                        }

                        <FormSubHeading>Image Height:</FormSubHeading>
                        <ABInputField
                            value={imageHeight || ''}
                            onChange={handleImageHeightChange}
                            placeholder="height"
                        />

                        <FormSubHeading>Image Width:</FormSubHeading>
                        <ABInputField
                            value={imageWidth || ''}
                            onChange={handleImageWidthChange}
                            placeholder="width"
                        />

                        <FormSubHeading>Image Link:</FormSubHeading>
                        <ABInputField
                            value={imageLink}
                            onChange={handleImageLinkChange}
                            placeholder="Image Link"
                        />

                        <FormSubHeading>Learn More Link:</FormSubHeading>
                        <ABInputField
                            value={link}
                            onChange={handleLinkChange}
                            placeholder="Link"
                        />

                        <CheckboxStyling>
                            <FormSubHeading>Sponsored:</FormSubHeading>
                            <input
                                type="checkbox"
                                checked={featuredContent}
                                onChange={handleFeaturedToggle}
                            />
                        </CheckboxStyling>

                        {/* <FormSubHeading>View More Link:</FormSubHeading>
                        <ABInputField
                            value={viewMoreLink}
                            onChange={handleViewMoreLinkChange}
                            placeholder="View More Link"
                        /> */}
                        <FormSubHeading>Attribution Tracking:</FormSubHeading>
                        <ABInputField
                            value={attribution}
                            onChange={handleAttributionChange}
                            placeholder="Attribution"
                        />

                        <FormSubHeading>Client ID:</FormSubHeading>
                        {
                            (clients && clients.length > 0) &&
                            <StyledSelect
                                name="clientId"
                                value={clientId}
                                onChange={handleSelectChange}
                                placeholder="Enter Client Id"
                            >
                                <option value="">Select</option>
                                {clients.map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.company} - {client.id}
                                    </option>
                                ))}
                            </StyledSelect>
                        }
                    </AlertFormRight>
                </FormFlex>

                <ActionButtons>
                    <RightButton>
                        <ABButton title="Save" onClick={() => onSave()} />
                    </RightButton>
                </ActionButtons>
                <ToastContainer />
            </PageWrapper>
        </>
    );
}

export default AlertPatentForm;
